import { useInfiniteQuery } from '@tanstack/react-query';
import { getUserRecommendations, getVideoRecommendations } from '@/APIs/recommendationsApi';
import { getResumeListCards } from '@/APIs/resumeApi';
import useAuth from '@/context/auth/useAuth';

export default function useRecommendations({
    videoId,
    fetchUserRecommendations = false,
    fetchVideoRecommendations = false
}) {
    const { babyMode } = useAuth();
    const {
        isLoading: recommendationsLoading,
        data: recommendationsData,
        error: recommendationsError,
        fetchNextPage: recommendationsDataFetchNextPage,
        isFetching: recommendationsDataIsFetching,
        isFetchingNextPage: recommendationsIsFetchingNextPage
    } = useInfiniteQuery({
        queryKey: ['getUserRecommendations'],
        queryFn: async (input) => {
            try {
                const recommData = await getUserRecommendations({
                    links: input?.pageParam,
                    params: {
                        age_level: babyMode ? 'babies' : 'kids'
                    }
                });
                const cardsData = await getResumeListCards({
                    type: 'video',
                    data: recommData.results
                });

                return { ...cardsData, links: recommData?.links };
            } catch (e) {
                console.log(e);
            }
        },
        getNextPageParam: (lastPage) => {
            return lastPage?.links?.next ? lastPage.links : undefined;
        },
        retry: (retryCount, error) => {
            return !error?.response?.status === 404;
        },
        staleTime: 30 * 60 * 1000,
        enabled: fetchUserRecommendations
    });

    const {
        isLoading: videoRecommendationsLoading,
        data: videoRecommendationsData,
        error: videoRecommendationsError,
        fetchNextPage: videoRecommendationsDataFetchNextPage,
        isFetching: videoRecommendationsDataIsFetching,
        isFetchingNextPage: videoRecommendationsIsFetchingNextPage
    } = useInfiniteQuery({
        queryKey: ['getVideoRecommendations', videoId],
        queryFn: async (input) => {
            try {
                const recommData = await getVideoRecommendations({
                    links: input?.pageParam,
                    params: {
                        video_id: videoId,
                        age_level: babyMode ? 'babies' : 'kids'
                    }
                });
                const cardsData = await getResumeListCards({
                    type: 'video',
                    data: recommData.results
                });

                return { ...cardsData, links: recommData?.links };
            } catch (e) {
                console.log(e);
            }
        },
        getNextPageParam: (lastPage) => {
            return lastPage?.links?.next ? lastPage.links : undefined;
        },
        retry: (retryCount, error) => {
            return !error?.response?.status === 404;
        },
        staleTime: 30 * 60 * 1000,
        enabled: fetchVideoRecommendations
    });

    return {
        recommendationsData: fetchVideoRecommendations
            ? videoRecommendationsData
            : recommendationsData,
        recommendationsLoading: fetchVideoRecommendations
            ? videoRecommendationsLoading
            : recommendationsLoading,
        recommendationsError: fetchVideoRecommendations
            ? videoRecommendationsError
            : recommendationsError,
        recommendationsDataFetchNextPage: fetchVideoRecommendations
            ? videoRecommendationsDataFetchNextPage
            : recommendationsDataFetchNextPage,
        recommendationsDataIsFetching: fetchVideoRecommendations
            ? videoRecommendationsDataIsFetching
            : recommendationsDataIsFetching,
        recommendationsIsFetchingNextPage: fetchVideoRecommendations
            ? videoRecommendationsIsFetchingNextPage
            : recommendationsIsFetchingNextPage
    };
}
